/**
 * Utility functions for the PlayerProfile components
 */

/**
 * Filters player games based on various filters
 * @param {Array} players - All player data
 * @param {String} playerName - Player name to filter for
 * @param {Array} activeFilters - Array of active filters
 * @returns {Array} - Filtered player games
 */
export const getFilteredGames = (players, playerName, activeFilters = []) => {
  if (!players || !playerName) return [];
  
  // Start with all games for this player
  return players
    .filter(game => game.player_name === playerName)
    .filter(game => {
      // Apply each active filter
      for (const filter of activeFilters) {
        switch (filter.type) {
          case 'home':
            if (!game.is_home_game) return false;
            break;
          case 'away':
            if (game.is_home_game) return false;
            break;
          case 'wins': {
            const isHome = game.is_home_game;
            const homeScore = parseInt(game.home_score);
            const awayScore = parseInt(game.away_score);
            const isWin = isHome ? homeScore > awayScore : awayScore > homeScore;
            if (!isWin) return false;
            break;
          }
          case 'losses': {
            const isHome = game.is_home_game;
            const homeScore = parseInt(game.home_score);
            const awayScore = parseInt(game.away_score);
            const isWin = isHome ? homeScore > awayScore : awayScore > homeScore;
            if (isWin) return false;
            break;
          }
          case 'venue':
            if (game.venue !== filter.value) return false;
            break;
          case 'opposition': {
            // Get the opponent based on whether it's a home or away game
            const opponent = game.is_home_game ? game.away_team : game.home_team;
            if (opponent !== filter.value) return false;
            break;
          }
          // Add any other filter types as needed
        }
      }
      return true;
    });
};

/**
 * Translates stat category names for display
 * @param {String} statCategory - The stat category code
 * @returns {String} Formatted stat name
 */
export const getStatName = (statCategory) => {
  switch (statCategory) {
    case 'all_run_metres':
      return 'Run Metres';
    case 'tries':
      return 'Tries';
    case 'tackles_made':
      return 'Tackles';
    case 'tackle_breaks':
      return 'Tackle Breaks';
    case 'line_breaks':
      return 'Line Breaks';
    case 'kick_metres':
      return 'Kick Metres';
    case 'points':
      return 'Points';
    case 'try_assists':
      return 'Try Assists';
    case 'line_break_assists':
      return 'Line Break Assists';
    case 'offloads':
      return 'Offloads';
    case 'dummy_passes':
      return 'Dummy Passes';
    case 'dummy_half_runs':
      return 'Dummy Half Runs';
    case 'post_contact_metres':
      return 'Post Contact Metres';
    case 'kicks':
      return 'Kicks';
    case 'forced_drop_outs':
      return 'Forced Drop Outs';
    case 'bomb_kicks':
      return 'Bomb Kicks';
    case 'grubbers':
      return 'Grubbers';
    case 'cross_field_kicks':
      return 'Cross Field Kicks';
    case 'missed_tackles':
      return 'Missed Tackles';
    case 'ineffective_tackles':
      return 'Ineffective Tackles';
    case 'intercepts':
      return 'Intercepts';
    case 'kicks_defused':
      return 'Kicks Defused';
    case 'one_on_one_steal':
      return 'One on One Steals';
    case 'errors':
      return 'Errors';
    case 'penalties':
      return 'Penalties';
    case 'ruck_infringements':
      return 'Ruck Infringements';
    case 'inside_10_metres':
      return 'Inside 10m';
    case 'handling_errors':
      return 'Handling Errors';
    default:
      // Convert snake_case to Title Case
      return statCategory.split('_').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      ).join(' ');
  }
};

/**
 * Gets the most recent game for a player
 * @param {Array} players - All player data
 * @param {String} playerName - Player name
 * @returns {Object} Most recent game for player
 */
export const getMostRecentGame = (players, playerName) => {
  if (!players || !playerName) return null;
  
  return players
    .filter(p => p.player_name === playerName && p.date)
    .sort((a, b) => new Date(b.date) - new Date(a.date))[0];
};

/**
 * Gets a player by name from player array
 * @param {Array} players - All player data
 * @param {String} playerName - Player name to find
 * @returns {Object} Player object if found
 */
export const findPlayerByName = (players, playerName) => {
  if (!players || !playerName) return null;
  return players.find(p => p.player_name === playerName);
};

/**
 * Gets teammates of a player
 * @param {Array} players - All player data
 * @param {Object} player - Player object
 * @param {Array} selectedSeasons - Selected seasons to filter by
 * @returns {Array} List of teammate names
 */
export const getTeamPlayers = (players, player, selectedSeasons) => {
  if (!players || !player || !selectedSeasons || selectedSeasons.length === 0) return [];
  
  const playerTeam = player.player_team;
  return [...new Set(players
    .filter(p => p.player_team === playerTeam && selectedSeasons.includes(p.year))
    .map(p => p.player_name))].sort();
};

/**
 * Checks if a filter is active
 * @param {Array} activeFilters - Array of active filters
 * @param {String} filterType - Filter type to check
 * @param {String} filterValue - Optional filter value (for filters with values)
 * @returns {Boolean} True if filter is active
 */
export const isFilterActive = (activeFilters, filterType, filterValue = null) => {
  return activeFilters.some(f => 
    f.type === filterType && (filterType !== 'venue' && filterType !== 'weather' || f.value === filterValue)
  );
};

/**
 * Converts a statistic category to its equivalent in odds data
 * @param {String} statCategory - The stat category
 * @returns {String} Equivalent odds category
 */
export const convertStatCategoryForOdds = (statCategory) => {
  const statMap = {
    'all_run_metres': 'runMetres',
    'tackles_made': 'tackles',
    'tackle_breaks': 'tackleBreaks',
    'line_breaks': 'lineBreaks',
    'kick_metres': 'kickMetres',
    'tries': 'tries',
    'points': 'points'
    // Add more mappings as needed
  };
  
  return statMap[statCategory] || statCategory;
}; 